import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { type Lang, t as translate, type TranslationKey } from '../lib/i18n';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem('agos_lang') as Lang | null;
    // Auto-detect Filipino users by browser language
    if (!stored) {
      const browser = navigator.language.toLowerCase();
      if (browser.startsWith('fil') || browser.startsWith('tl')) return 'fil';
    }
    return stored ?? 'en';
  });

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem('agos_lang', newLang);
    document.documentElement.lang = newLang === 'fil' ? 'fil' : 'en';
  };

  useEffect(() => {
    document.documentElement.lang = lang === 'fil' ? 'fil' : 'en';
  }, [lang]);

  const t = (key: TranslationKey) => translate(lang, key);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
